import { ComponentType, createContext, FC, JSX, useContext, useMemo } from 'react';

import { useAuthentication } from './auth-provider';

type AuthenticationContext = {
  organisation: string;
};

const AuthContext = createContext<AuthenticationContext>({
  organisation: '',
});

const useAuthenticationContext = () => useContext(AuthContext);

function withAuthenticationContext<P extends JSX.IntrinsicAttributes>(WrappedComponent: ComponentType<P>): FC<P> {
  const Hoc: FC<P> = (props) => {
    const { organisation } = useAuthentication();
    const context = useMemo(
      () =>
        organisation
          ? {
              organisation,
            }
          : null,
      [organisation]
    );

    if (!context) return null;

    return (
      <AuthContext.Provider value={context}>
        <WrappedComponent {...props} />
      </AuthContext.Provider>
    );
  };

  const displayName = WrappedComponent.displayName ?? WrappedComponent.name ?? 'Component';
  Hoc.displayName = `withAuthenticationContext(${displayName})`;

  return Hoc;
}

export { AuthContext, useAuthenticationContext, withAuthenticationContext };
export type { AuthenticationContext };
